import { ReactNode } from 'react';
import {
  TeamIntegrationFeature,
  TeamIntegrationProvider,
} from '../../../../typings/TeamIntegration.interface';
import { IconName } from '../components/common/base/Icon';
import { Div } from '../components/common/helpers/StyledUtils';
import Text from '../components/common/base/Text';

type Fields = {
  label: string;
  name: string;
  type: 'text' | 'select' | 'password' | 'number' | 'radio';
  placeholder?: string;
  required?: boolean;
  options?: { value: string; label: string }[];
  inline?: boolean;
}[];

export interface TeamIntegrationSchema {
  icon: IconName;
  label: string;
  features: TeamIntegrationFeature[];
  fields?: Fields;
  how_steps?: string[];
  onClick?: () => void;
  callback?: (data: any) => void;
  configure_step?: {
    label: string;
    description: string;
    button_text: string;
    button_href: string;
    callback?: (data: any) => void;
  };
  alert_banner?: ReactNode;
  /** Reconnect re-authorizes in place, keeping issue trigger channels. */
  supports_reauthorize?: boolean;
  /** Toast shown when the OAuth/install callback can't be trusted, e.g. it expired. */
  callback_error?: string;
  /** Warning after disconnecting when cleaning up in the provider failed. */
  cleanup_failed_message?: string;
  /**
   * Returns a message to confirm before completing a callback this tab didn't start (e.g.
   * an install started from the provider), or null when no confirmation is needed.
   */
  confirm_callback?: (data: Record<string, string>) => ReactNode | null;
}

const SENTRY_INSTALL_KEY = 'integrations:sentry:install';
// How long an install started from this browser tab stays valid.
const SENTRY_INSTALL_MAX_AGE_MS = 15 * 60 * 1000;

const team_integration_schemas: {
  [key in Exclude<TeamIntegrationProvider, 'email'>]: TeamIntegrationSchema;
} = {
  slack: {
    icon: 'slack_colored',
    label: 'Slack',
    features: ['notification'],
    onClick: () => {
      window.location.replace(
        `${process.env.SLACK_SHARABLE_URL}&redirect_uri=${process.env.SLACK_REDIRECT_URI}`,
      );
    },
    callback: (data: any) => {
      return {
        access_token: data.code,
      };
    },
    alert_banner: (
      <Div>
        To connect Hookdeck to a private Slack channel, first type{' '}
        <Text display="inline-block" mono bold>
          /invite @Hookdeck
        </Text>{' '}
        in the channel. The channel will then populate in the dropdown below.
      </Div>
    ),
  },
  opsgenie: {
    icon: 'opsgenie_colored',
    label: 'Opsgenie',
    features: ['notification'],
    fields: [{ label: 'API key', name: 'api_key', type: 'password' }],
    how_steps: [
      'Head to your Team’s Dashboard,',
      'Click <b>Teams</b> in the top nav bar,',
      'Select a <b>Team</b> or <b>Add Team</b>,',
      'In the Team’s page, navigate to <b>Integrations</b> from the left sidebar,',
      'Click <b>Add Integration</b> and search for <b>API</b>,',
      'Enter your <b>API Key</b> in the next section,',
      'Enable <b>Read Access</b>, <b>Create and Update Access</b>, and <b>Enabled</b>,',
      'Select <b>Save Integration</b>.',
    ],
  },
  pagerduty: {
    icon: 'pagerduty',
    label: 'PagerDuty',
    features: ['notification'],
    onClick: () => {
      window.location.replace(
        `https://app.pagerduty.com/install/integration?app_id=${process.env.PAGERDUTY_APP_ID}&redirect_url=${process.env.PAGERDUTY_REDIRECT_URL_ROOT + '/configure'}&version=2`,
      );
    },
    // Pagerduty has 2 "oauth" steps, one for the "integration key" and one for the oauth code. Because it's not standard we're adding a manual step here called "configure"
    configure_step: {
      label: 'Authorize Hookdeck with Pagerduty',
      description:
        'Beyond providing your target service to link with Hookdeck. You also need to grant authorization to Hookdeck to use the Pagerduty API.',
      button_text: 'Authorize',
      button_href: `https://identity.pagerduty.com/oauth/authorize?client_id=${process.env.PAGERDUTY_CLIENT_ID}&scope=write&response_type=code&redirect_uri=${process.env.PAGERDUTY_REDIRECT_URL_ROOT + '/connect'}`,
      callback: (data: { config: string }) => {
        sessionStorage.setItem('integrations:pagerduty:integration_keys', data.config);
      },
    },
    callback: (data: any) => {
      const storage_keys = sessionStorage.getItem('integrations:pagerduty:integration_keys');

      if (!storage_keys) {
        throw new Error('No integration keys found');
      }

      const parsed_keys = JSON.parse(storage_keys);

      return {
        services: parsed_keys.integration_keys.filter((service) => service.type === 'service'),
        oauth_code: data.code,
      };
    },
  },
  datadog: {
    icon: 'datadog',
    label: 'Datadog',
    features: ['metrics'],
    fields: [
      { label: 'API key', name: 'api_key', type: 'password' },
      {
        label: 'Site Parameter',
        name: 'site',
        type: 'text',
        placeholder: 'datadoghq.com',
      },
    ],
    how_steps: [
      'You must be on a paid Datadog plan to use this integration. Free plans do not support Custom Metrics/Events.',
      'Head to your Datadog Dashboard,',
      'Click <b>Organization Settings</b> from the left sidebar,',
      'Select <b>API Keys</b> in the settings list,',
      'In the API Keys page, click <b>New Key</b> to generate a new API key,',
      'Enter the <b>API Key</b> in the below API Key field,',
      'Enter the <b>Site Parameter</b> in the below Site Parameter field for based on the Datadog Site you are using, see <a href="https://docs.datadoghq.com/getting_started/site/#access-the-datadog-site" target="_blank">here</a> for more information.',
      'Click <b>Connect</b> to save the integration setup to Datadog, your metrics will start sending to Datadog within a few minutes.',
    ],
  },
  new_relic: {
    icon: 'new_relic',
    label: 'New Relic',
    features: ['metrics'],
    fields: [
      { label: 'API Key', name: 'api_key', type: 'password' },
      {
        label: 'Account ID',
        name: 'account_id',
        type: 'text',
        placeholder: 'Your New Relic Account ID',
      },
    ],
    how_steps: [
      'Head to your New Relic Dashboard,',
      'Navigate to <b>API Keys</b> from the user menu in the top right,',
      'Click <b>Create a key</b> to generate a new API key,',
      '<b>Important:</b> Select <b>Ingest - License</b> as the key type (not User API key),',
      'Copy the generated <b>License Key</b> and enter it in the API Key field below,',
      'To find your Account ID, go to the New Relic homepage and look for your Account ID in the URL or account switcher,',
      'Enter your <b>Account ID</b> in the Account ID field below,',
      'Click <b>Connect</b> to save the integration setup, your metrics will start sending to New Relic within a few minutes.',
    ],
  },
  dynatrace: {
    icon: 'dynatrace',
    label: 'Dynatrace',
    features: ['metrics'],
    fields: [
      { label: 'Access token', name: 'api_token', type: 'password' },
      {
        label: 'Environment URL',
        name: 'environment_url',
        type: 'text',
        placeholder: 'https://abc12345.live.dynatrace.com',
      },
    ],
    how_steps: [
      'Only Dynatrace SaaS environments are supported (<b>*.live.dynatrace.com</b> or <b>*.apps.dynatrace.com</b>).',
      'Sign in to your Dynatrace environment, e.g. https://abc12345.apps.dynatrace.com (not Account Management),',
      'Open the <b>Access Tokens</b> app (search for it with Ctrl/Cmd+K). Hookdeck needs an access token, not a platform token,',
      'Click <b>Generate new token</b> and enter a name for your token,',
      'Add the <b>Ingest metrics</b> (metrics.ingest) scope,',
      'Click <b>Generate token</b>, then copy the token (it starts with <b>dt0c01.</b>) into the Access token field below,',
      'Enter your <b>Environment URL</b>, e.g. https://abc12345.live.dynatrace.com. You can copy it from your browser while signed in to Dynatrace,',
      'Click <b>Connect</b> to save the integration setup, your metrics will start sending to Dynatrace within a few minutes.',
    ],
  },
  prometheus: {
    icon: 'prometheus',
    label: 'Prometheus',
    features: ['metrics'],
    fields: [], // No configuration fields needed - Prometheus pulls from /metrics endpoint
    how_steps: [
      'Configure your Prometheus server to scrape metrics from your Hookdeck endpoint:',
      '<b>Add this job to your prometheus.yml configuration:</b>',
      '<pre>- job_name: "hookdeck"<br/>  static_configs:<br/>    - targets: ["https://api.hookdeck.com"]<br/>  metrics_path: "/metrics/prometheus"<br/>  scrape_interval: 30s</pre>',
      'Reload your Prometheus configuration or restart Prometheus',
      'Your Hookdeck metrics (requests, events, attempts, queue depth) will now be available in Prometheus',
      'Use labels like team_id, source_name, destination_name to filter metrics by your resources',
    ],
  },
  microsoft_teams: {
    icon: 'microsoft_teams',
    label: 'Microsoft Teams',
    features: ['notification'],
    onClick: () => {
      // Must be accessed as literal `process.env.X` expressions — Next.js inlines
      // these at build time; optional chaining breaks the replacement and yields
      // `undefined` in the browser.
      const redirect_uri = process.env.MICROSOFT_TEAMS_REDIRECT_URI;
      const client_id = process.env.MICROSOFT_TEAMS_CLIENT_ID;
      const scopes =
        'Channel.ReadBasic.All ChannelMessage.Send Team.ReadBasic.All ChannelSettings.Read.All';
      const url = `https://login.microsoftonline.com/common/oauth2/v2.0/authorize?client_id=${client_id}&response_type=code&redirect_uri=${redirect_uri}&scope=${encodeURIComponent(scopes)}`;
      window.location.replace(url);
    },
    callback: (data: any) => {
      return {
        access_token: data.code,
      };
    },
  },
  discord: {
    icon: 'discord',
    label: 'Discord',
    features: ['notification'],
    onClick: () => {
      // Same as microsoft_teams above: keep literal `process.env.X` so Next.js
      // can inline the values at build time.
      const redirect_uri = process.env.DISCORD_REDIRECT_URI;
      const client_id = process.env.DISCORD_CLIENT_ID;
      const scopes = 'bot guilds';
      const permissions = '536870912'; // MANAGE_WEBHOOKS permission
      const url = `https://discord.com/oauth2/authorize?client_id=${client_id}&response_type=code&redirect_uri=${redirect_uri}&scope=${encodeURIComponent(scopes)}&permissions=${permissions}`;
      window.location.replace(url);
    },
    callback: (data: any) => {
      return {
        access_token: data.code,
        guild_id: data.guild_id, // Pass the guild_id from Discord OAuth
      };
    },
    alert_banner: (
      <Div>
        To connect Hookdeck to a private Discord channel, you must have the{' '}
        <Text display="inline-block" mono bold>
          Manage Webhooks
        </Text>{' '}
        permission in that channel. If you don't see a channel in the dropdown, check your Discord
        permissions.
      </Div>
    ),
  },
  betteruptime: {
    icon: 'betteruptime',
    label: 'Better Uptime',
    features: ['notification'],
    fields: [{ label: 'API Token', name: 'api_token', type: 'password', required: true }],
    how_steps: [
      'Head to your Better Uptime account,',
      'Click on your <b>Team Settings</b> in the top navigation,',
      'Select <b>API tokens</b> from the left sidebar,',
      'Click <b>Create API token</b>,',
      'Enter a name for the token (e.g., "Hookdeck Integration"),',
      'Copy the generated <b>API Token</b> and enter it in the field below,',
      'Click <b>Connect</b> to complete the integration.',
    ],
    alert_banner: (
      <Div>
        Better Uptime integration creates incidents for your issues and manages their lifecycle.
        Select an escalation policy when configuring issue triggers to route notifications to your
        on-call team.
      </Div>
    ),
  },
  incidentio: {
    icon: 'incidentio',
    label: 'incident.io',
    features: ['notification'],
    fields: [
      { label: 'API Key', name: 'api_key', type: 'password', required: true },
      { label: 'Webhook Signing Secret', name: 'webhook_secret', type: 'password' },
    ],
    how_steps: [
      'Head to your incident.io account,',
      'Navigate to <b>Settings > Integrations > API Keys</b>,',
      'Click <b>Create API Key</b> with permissions to create and update incidents,',
      'Copy the generated <b>API Key</b> and enter it in the API Key field below,',
      `To enable bidirectional sync, go to <b>Settings > Webhooks</b> in incident.io,`,
      `Add a new webhook endpoint with URL: <b>${process.env.HOOKDECK_INCIDENTIO_WEBHOOK_URL || 'https://hkdk.events/10ovmt8e8dla4v'}</b>,`,
      'Subscribe to <b>incident-v2.updated-public</b> events,',
      'Copy the <b>Signing Secret</b> from the webhook settings into the Webhook Signing Secret field below,',
      'Click <b>Connect</b> to complete the integration.',
    ],
    alert_banner: (
      <Div>
        incident.io integration creates incidents for your issues and manages their lifecycle. To
        enable bidirectional sync (incident.io updates reflected in Hookdeck), configure a webhook
        in your incident.io settings pointing to:{' '}
        <Text display="inline-block" mono bold>
          {process.env.HOOKDECK_INCIDENTIO_WEBHOOK_URL || 'https://hkdk.events/10ovmt8e8dla4v'}
        </Text>
      </Div>
    ),
  },
  sentry: {
    icon: 'sentry',
    label: 'Sentry',
    features: ['notification'],
    supports_reauthorize: true,
    callback_error: 'This Sentry install link is incomplete. Connect Sentry again from this page.',
    cleanup_failed_message:
      "Sentry was disconnected, but Hookdeck couldn't uninstall itself from your Sentry organization. Uninstall it in Sentry under Settings → Integrations before connecting again.",
    onClick: () => {
      // Sentry's install flow doesn't round-trip a state value, so remember that this tab
      // started an install: a callback this tab didn't start is only completed once the
      // user confirms the Sentry organization (see confirm_callback).
      sessionStorage.setItem(SENTRY_INSTALL_KEY, String(Date.now()));
      window.location.replace(
        `https://sentry.io/sentry-apps/${process.env.SENTRY_APP_SLUG}/external-install/`,
      );
    },
    confirm_callback: (data) => {
      const started_at = Number(sessionStorage.getItem(SENTRY_INSTALL_KEY));
      if (started_at && Date.now() - started_at <= SENTRY_INSTALL_MAX_AGE_MS) {
        return null;
      }
      // Protects against a link that connects this project to someone else's Sentry.
      return (
        <>
          Connect this Hookdeck project to the Sentry organization{' '}
          <Text as={'span'} bold>
            {data.orgSlug}
          </Text>
          ? Only continue if you just installed Hookdeck in that Sentry organization.
        </>
      );
    },
    callback: (data: { code: string; installationId?: string; orgSlug?: string }) => {
      sessionStorage.removeItem(SENTRY_INSTALL_KEY);

      if (!data.installationId || !data.orgSlug) {
        throw new Error('This Sentry install link is incomplete');
      }

      return {
        code: data.code,
        installation_id: data.installationId,
        org_slug: data.orgSlug,
      };
    },
    alert_banner: (
      <Div>
        Hookdeck creates a Sentry issue for each Hookdeck issue. Resolving, archiving, reopening or
        assigning it in either tool updates the other. Each issue counts as one event against your
        Sentry error quota.
      </Div>
    ),
  },
};

export default team_integration_schemas;
